import EmblaCarousel from "embla-carousel";
import { addDotBtnsAndClickHandlers } from "./modules/EmblaCarouselDotButton";
import Autoplay from "embla-carousel-autoplay";
import { gsap } from "gsap";
import ShuffleText from "shuffle-text";
import { VideoPlayer } from "./modules/videoPlayer";
import { mq } from "./modules/utils";
/**
 * @file main.js
 * 予約カレンダーを JSON から読み込んだデータで初期化（axios 使用）。
 */
import axios from "axios";
import { ReservationCalendar } from "./modules/reservation-calendar";
import { fetchSimbaNews } from "./modules/fetchComponentContents";
import { initGoodsFilter } from "./modules/goods-filter";
import { initItazura } from "./modules/itazura";
import { resolveScheduledJsonUrl } from "./modules/content-schedule";
import SimpleBar from "simplebar";
import "simplebar/dist/simplebar.css";

/** @typedef {"○"|"△"|"×"} AvailabilityStatus */
/**
 * @typedef AvailabilityItem
 * @property {string} date   - ISO日付 YYYY-MM-DD
 * @property {AvailabilityStatus} status
 * @property {string=} url   - △/○ のときのリンク先
 * @property {string=} label - リンクラベル（省略時 "空室状況"）
 */

/**
 * 予約可否JSONを取得する（axios）
 * @param {string} url - 例: "/data/availability.json"
 * @returns {Promise<AvailabilityItem[]>}
 */
async function loadAvailability(url) {
  try {
    // キャッシュ抑止用にタイムスタンプを付与（必要なければ削除可）
    const res = await axios.get(url, { params: { t: Date.now() } });
    const data = res?.data?.availabilities;
    if (!Array.isArray(data)) {
      console.warn("[availability] JSONが配列ではありません:", data);
      return [];
    }
    // 最低限のバリデーション
    return data.filter((r) => r && typeof r.date === "string" && typeof r.availableCount === "number");
  } catch (err) {
    console.error("[availability] 取得に失敗:", err);
    return [];
  }
}

/**
 * 休日・試合日JSONを取得（axios）
 * 形式: { "YYYY-MM-DD": { isHoliday: boolean, isGameday: boolean, gameTimePattern?: string|null } }
 * @param {string} url
 * @returns {Promise<Map<string, {isHoliday:boolean,isGameday:boolean,gameTimePattern:string|null}>>>}
 */
async function loadHolidayAndGameday(url) {
  try {
    const res = await axios.get(url, { params: { t: Date.now() } });
    const obj = res?.data || {};
    const map = new Map();
    for (const [date, v] of Object.entries(obj)) {
      if (typeof date === "string" && v && typeof v === "object") {
        map.set(date, {
          isHoliday: Boolean(v.isHoliday),
          isGameday: Boolean(v.isGameday),
          gameTimePattern: v.gameTimePattern ?? null,
        });
      }
    }
    return map;
  } catch (err) {
    console.error("[holiday] 取得に失敗:", err);
    return new Map();
  }
}

(async () => {
  // DOM取得
  const calendarEl = document.getElementById("calendar");
  const ymSelectEl = document.getElementById("ymSelect");
  const prevBtnEl = document.getElementById("prevBtn");
  const nextBtnEl = document.getElementById("nextBtn");
  const loadingEl = document.getElementById("loading");

  // 人数選択フォームの要素を取得
  const adultsSelectEl = document.getElementById("adultsSelect");
  const childrenSelectEl = document.getElementById("childrenSelect");
  const infantsSelectEl = document.getElementById("infantsSelect");

  // 先にJSONを取得 → 初期化時に注入（初回描画を1回で完了）
  const [availabilityRows, holidayMap] = await Promise.all([loadAvailability(__JSON_URL__), loadHolidayAndGameday(__HOLIDAY_JSON_URL__)]);

  // インスタンス化（当月〜7ヶ月後／当日含む・過去は非活性）
  const cal = new ReservationCalendar({
    calendarEl,
    ymSelectEl,
    prevBtnEl,
    nextBtnEl,
    loadingEl, // 省略可：あれば切替時にローディング演出
    adultsSelectEl, // 大人の人数セレクト
    childrenSelectEl, // 小学生の人数セレクト
    infantsSelectEl, // 幼児の人数セレクト
    monthsAhead: 6, // 当月〜6ヶ月後（計7ヶ月）
    availability: availabilityRows,
    holidayMap: holidayMap,
    defaultLinkLabel: "空室状況",
    onMonthChange: (viewDate) => {
      // 必要ならトラッキング等
      // console.log("[month change]", viewDate);
    },
  });

  cal.init();

  // 後から再読込したい場合の例：
  // const refreshed = await loadAvailability("/data/availability.json");
  // cal.setAvailability(refreshed);
})();

(async () => {
  const goodsSection = document.querySelector(".goods-section");
  if (goodsSection) {
    await initGoodsFilter({
      sectionEl: goodsSection,
      jsonUrl: await resolveScheduledJsonUrl("./data/goods.json"),
    });
  }
})();

(async () => {
  const itazuraSection = document.querySelector(".itazura-section");
  if (itazuraSection) {
    await initItazura({
      sectionEl: itazuraSection,
      jsonUrl: await resolveScheduledJsonUrl("./data/itazura.json"),
    });
  }
})();

// 後で使用するためのbody要素への参照
const BODY = document.getElementsByTagName("body");

const OPTIONS = {
  dragFree: false,
  loop: true,
  align: "start",
  containScroll: "trimSnaps",
};

const STEP_EMBLA_OPTIONS = {
  dragFree: false,
  loop: false,
  align: "start",
  containScroll: "trimSnaps",
};

/**
 * ヘッダー上部のニュースティッカーを動的生成する関数（1件を横スクロールで繰り返し表示）
 */
const initFixedNewsTicker = async () => {
  const container = document.querySelector(".fixed-news-list");
  const track = container?.querySelector(".fixed-news-list__track");
  if (!container || !track) return;

  const newsItems = await fetchSimbaNews();
  if (newsItems.length === 0) {
    container.style.display = "none";
    return;
  }

  const item = newsItems[0];
  const TICKER_SPEED = 80; // px/sec

  const createNewsItem = () => {
    const li = document.createElement("li");

    if (item.new) {
      const img = document.createElement("img");
      img.src = "./assets/img/icon-news.svg";
      img.alt = "News";
      img.width = 35;
      img.height = 26;
      li.appendChild(img);
    }

    const link = document.createElement("a");
    link.className = "hover";
    link.href = item.url;
    link.textContent = item.text;
    li.appendChild(link);

    return li;
  };

  const fillList = (ul) => {
    ul.appendChild(createNewsItem());
    while (ul.scrollWidth < window.innerWidth) {
      ul.appendChild(createNewsItem());
    }
  };

  const listA = document.createElement("ul");
  track.appendChild(listA);
  fillList(listA);

  const listB = document.createElement("ul");
  listB.setAttribute("aria-hidden", "true");
  const itemCount = listA.children.length;
  for (let i = 0; i < itemCount; i++) {
    listB.appendChild(createNewsItem());
  }
  track.appendChild(listB);

  const setTickerDuration = () => {
    const halfWidth = track.scrollWidth / 2;
    const duration = Math.max(halfWidth / TICKER_SPEED, 10);
    track.style.setProperty("--ticker-duration", `${duration}s`);
  };

  setTickerDuration();
  window.addEventListener("resize", setTickerDuration);
};

/**
 * ニュースの日付値を取得（date / data の両方に対応）
 * @param {{ date?: string, data?: string }} item
 * @returns {string}
 */
const getNewsDateValue = (item) => String(item.date ?? item.data ?? "").trim();

/**
 * ニュース日付を表示用に整形（YYYY.MM.DD）
 * @param {string} [value]
 * @returns {string}
 */
const formatNewsDate = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return "";

  const isoDateMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDateMatch) {
    return isoDateMatch[1].replace(/-/g, ".");
  }

  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) {
    return new Intl.DateTimeFormat("ja-JP", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .format(parsed)
      .replace(/\//g, ".");
  }

  return normalized.replace(/-/g, ".");
};

/**
 * time要素のdatetime属性用に整形（YYYY-MM-DD）
 * @param {string} [value]
 * @returns {string}
 */
const toNewsDatetime = (value) => {
  const normalized = String(value ?? "").trim();
  if (!normalized) return "";

  const isoDateMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDateMatch) {
    return isoDateMatch[1];
  }

  const parsed = new Date(normalized);
  if (!Number.isNaN(parsed.getTime())) {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(parsed);
  }

  return normalized.replace(/\./g, "-").slice(0, 10);
};

const NEWS_SCROLL_THRESHOLD = 5;

/**
 * ニュースエリアの一覧を動的生成する関数
 */
const initNewsList = async () => {
  const newsScroll = document.querySelector(".fukidashi-news__scroll");
  const newsList = document.querySelector(".fukidashi-news__list");
  if (!newsList) return;

  const newsItems = await fetchSimbaNews();

  if (newsItems.length === 0) {
    const newsWrap = document.querySelector(".fukidashi-news");
    if (newsWrap) newsWrap.style.display = "none";
    return;
  }

  newsList.innerHTML = newsItems
    .map((item) => {
      const newsDateValue = getNewsDateValue(item);
      const displayDate = formatNewsDate(newsDateValue);

      return `
    <li>
      <a href="${item.url}" class="fwb hover" target="_blank" rel="noopener noreferrer">
        ${item.new ? '<img src="./assets/img/icon-news.svg" alt="News" width="66" height="48" />' : ""}
        ${displayDate ? `<time class="news-date fwb" datetime="${toNewsDatetime(newsDateValue)}">${displayDate}</time>` : ""}
        <span class="news-text">${item.text}</span>
      </a>
    </li>
  `;
    })
    .join("");

  if (!newsScroll) return;

  if (newsItems.length >= NEWS_SCROLL_THRESHOLD) {
    new SimpleBar(newsScroll, { autoHide: false });
    return;
  }

  newsScroll.classList.add("is-no-scroll");
};

// EmblaCarouselの初期化関数
const initEmblaCarousel = () => {
  const emblaNodes = document.querySelectorAll(".embla:not(.step-embla)");
  const emblaApis = [];

  emblaNodes.forEach((emblaNode, index) => {
    const viewportNode = emblaNode.querySelector(".embla__viewport");
    const dotsNode = emblaNode.querySelector(".embla__dots");

    if (!viewportNode) return;

    // オートプレイの設定を改善

    const slideCount = viewportNode.querySelectorAll(".embla__slide").length;
    if (slideCount <= 1) {
      emblaNode.classList.add("is-static");
      if (dotsNode) {
        dotsNode.classList.add("is-hidden");
      }
      return;
    }

    const autoplayOptions = {
      delay: 4000,
      stopOnInteraction: false, // マウス操作でもオートプレイを継続
      stopOnMouseEnter: false, // マウスホバーでも停止しない
      playOnInit: true,
    };

    // @ts-ignore
    const emblaApi = EmblaCarousel(viewportNode, OPTIONS, [Autoplay(autoplayOptions)]);
    emblaApis.push(emblaApi);

    const onNavButtonClick = (emblaApi) => {
      const autoplay = emblaApi?.plugins()?.autoplay;
      if (!autoplay) return;

      // オートプレイを一時停止してから再開
      autoplay.stop();
      setTimeout(() => {
        autoplay.play();
      }, 100);
    };

    if (dotsNode) {
      const removeDotBtnsAndClickHandlers = addDotBtnsAndClickHandlers(emblaApi, dotsNode, onNavButtonClick);
      emblaApi.on("destroy", removeDotBtnsAndClickHandlers);
    }

    // ドラッグ操作後にスライドを適切な位置に調整
    emblaApi.on("pointerUp", () => {
      emblaApi.scrollTo(emblaApi.selectedScrollSnap());
    });
  });

  return emblaApis;
};

const STEP_THUMB_COVERFLOW = {
  minScale: 0.75,
  maxScale: 1.05,
  minOpacity: 0.45,
  maxOpacity: 1,
  spread: 110,
};

/**
 * 旧 iTunes 風：ポインタに近いサムネほど拡大する
 * @param {HTMLElement} thumbsContainer
 * @param {HTMLElement[]} thumbNodes
 */
const initStepThumbCoverflow = (thumbsContainer, thumbNodes) => {
  if (!thumbsContainer || thumbNodes.length === 0) return;

  const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  if (!canHover) return;

  let frameId = null;

  const resetCoverflow = () => {
    thumbsContainer.classList.remove("is-coverflow-active");

    for (const thumb of thumbNodes) {
      thumb.style.removeProperty("--thumb-scale");
      thumb.style.removeProperty("--thumb-opacity");
      thumb.style.removeProperty("z-index");
    }
  };

  const updateCoverflow = (pointerX) => {
    thumbsContainer.classList.add("is-coverflow-active");

    const { minScale, maxScale, minOpacity, maxOpacity, spread } = STEP_THUMB_COVERFLOW;
    const spreadFactor = spread * spread * 2;

    thumbNodes.forEach((thumb) => {
      const rect = thumb.getBoundingClientRect();
      const thumbCenterX = rect.left + rect.width / 2;
      const distance = pointerX - thumbCenterX;
      const ratio = Math.exp(-(distance * distance) / spreadFactor);
      const scale = minScale + (maxScale - minScale) * ratio;
      const opacity = minOpacity + (maxOpacity - minOpacity) * ratio;

      thumb.style.setProperty("--thumb-scale", scale.toFixed(3));
      thumb.style.setProperty("--thumb-opacity", opacity.toFixed(3));
      thumb.style.zIndex = String(Math.round(scale * 100));
    });
  };

  thumbsContainer.addEventListener("mousemove", (event) => {
    if (frameId) cancelAnimationFrame(frameId);

    frameId = requestAnimationFrame(() => {
      updateCoverflow(event.clientX);
    });
  });

  thumbsContainer.addEventListener("mouseleave", () => {
    if (frameId) {
      cancelAnimationFrame(frameId);
      frameId = null;
    }

    resetCoverflow();
  });
};

const initStepEmblaCarousel = () => {
  const emblaNode = document.querySelector(".step-embla");
  if (!emblaNode) return;

  const viewportNode = emblaNode.querySelector(".embla__viewport");
  const thumbNodes = Array.from(emblaNode.querySelectorAll(".step-embla__thumb"));
  const thumbsContainer = emblaNode.querySelector(".step-embla__thumbs");
  const thumbsScroller = emblaNode.querySelector(".step-embla__thumbs-viewport") ?? thumbsContainer;
  const prevBtn = emblaNode.querySelector(".step-embla__prev");
  const nextBtn = emblaNode.querySelector(".step-embla__next");

  if (!viewportNode) return;

  const emblaApi = EmblaCarousel(viewportNode, STEP_EMBLA_OPTIONS);

  const scrollThumbIntoView = (index) => {
    const thumb = thumbNodes[index];
    if (!thumb || !thumbsScroller) return;

    const scrollerRect = thumbsScroller.getBoundingClientRect();
    const thumbRect = thumb.getBoundingClientRect();
    const delta =
      thumbRect.left + thumbRect.width / 2 - (scrollerRect.left + scrollerRect.width / 2);

    thumbsScroller.scrollTo({
      left: thumbsScroller.scrollLeft + delta,
      behavior: "smooth",
    });
  };

  const updateThumbState = () => {
    const selected = emblaApi.selectedScrollSnap();

    thumbNodes.forEach((thumb, index) => {
      const isSelected = index === selected;
      thumb.classList.toggle("is-selected", isSelected);
      thumb.setAttribute("aria-selected", isSelected ? "true" : "false");
    });

    scrollThumbIntoView(selected);

    if (prevBtn) {
      prevBtn.disabled = !emblaApi.canScrollPrev();
    }

    if (nextBtn) {
      nextBtn.disabled = !emblaApi.canScrollNext();
    }
  };

  thumbNodes.forEach((thumb, index) => {
    thumb.addEventListener("click", () => emblaApi.scrollTo(index));
  });

  prevBtn?.addEventListener("click", () => emblaApi.scrollPrev());
  nextBtn?.addEventListener("click", () => emblaApi.scrollNext());

  emblaApi.on("init", updateThumbState);
  emblaApi.on("select", updateThumbState);
  emblaApi.on("reInit", updateThumbState);

  emblaApi.on("pointerUp", () => {
    emblaApi.scrollTo(emblaApi.selectedScrollSnap());
  });

  if (thumbsScroller) {
    thumbsScroller.addEventListener(
      "wheel",
      (event) => {
        const maxScroll = thumbsScroller.scrollWidth - thumbsScroller.clientWidth;
        if (maxScroll <= 1) return;

        const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
        if (delta === 0) return;
        if (delta < 0 && thumbsScroller.scrollLeft <= 0) return;
        if (delta > 0 && thumbsScroller.scrollLeft >= maxScroll - 1) return;

        event.preventDefault();
        thumbsScroller.scrollLeft += delta;
      },
      { passive: false },
    );
  }

  initStepThumbCoverflow(thumbsContainer, thumbNodes);

  return emblaApi;
};

const hbgFunc = () => {
  const header = document.querySelector(".js-header-class");
  const hbg = document.querySelector(".js-hbg");
  const closeHbg = document.querySelectorAll(".js-closeHbg");
  const backdrop = document.querySelector(".sidebar-backdrop");
  const sidebarDesktopMq = window.matchMedia("(min-width: 1280px)");

  if (!hbg) return;

  const closeSidebarMenu = () => {
    header?.classList.remove("sub-menu-open");
    hbg.setAttribute("aria-expanded", "false");
  };

  hbg.addEventListener(
    "click",
    function (e) {
      e.preventDefault();
      if (sidebarDesktopMq.matches) return;

      const isOpen = header.classList.toggle("sub-menu-open");
      hbg.setAttribute("aria-expanded", isOpen ? "true" : "false");
    },
    false,
  );
  closeHbg.forEach((btn) => {
    btn.addEventListener(
      "click",
      function (e) {
        if (!e.target.href.split("#")[1]) e.preventDefault();
        closeSidebarMenu();
      },
      false,
    );
  });

  if (backdrop) {
    backdrop.addEventListener("click", closeSidebarMenu);
  }

  sidebarDesktopMq.addEventListener("change", (event) => {
    if (event.matches) {
      closeSidebarMenu();
    }
  });
};

// 対象の要素を取得
const targetElement = document.querySelector(".js-header-class");

if (targetElement) {
  // MutationObserverのコールバック関数
  const observerCallback = (mutationsList) => {
    mutationsList.forEach((mutation) => {
      if (mutation.type === "attributes" && mutation.attributeName === "class") {
        // js-header-classにsub-menu-openがある場合、bodyにopenを追加
        if (targetElement.classList.contains("sub-menu-open")) {
          document.body.classList.add("open2");
        } else {
          document.body.classList.remove("open2");
        }
      }
    });
  };

  // MutationObserverの設定
  const observer = new MutationObserver(observerCallback);

  // 対象の要素に対して監視を開始
  observer.observe(targetElement, { attributes: true });
}

let lastScrollTop = 0;
let scrollTimeout = null; // スクロールタイムアウトを管理

// Intersection Observer APIを使用して.kvが画面内に入った場合に処理を行う
const kvElement = document.querySelector(".kv");
if (kvElement && mq("pc")) {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          document.body.classList.add("inside-kv");
        } else {
          document.body.classList.remove("inside-kv");
        }
      });
    },
    {
      threshold: 0.1, // 要素の10%が見えるときにコールバック
    },
  );
  observer.observe(kvElement);
} else if (mq("sp")) {
  document.body.classList.add("inside-kv");
}

// Intersection Observer APIを使用してsectionが画面内に入った場合に.js-simba-animにopenクラスを付与する関数
const initSimbaAnimation = () => {
  const simbaElements = document.querySelectorAll(".js-simba-anim");

  if (simbaElements.length === 0) return;

  simbaElements.forEach((simbaElement) => {
    // 親要素のsectionを取得
    const parentSection = simbaElement.closest("section");

    if (!parentSection) return;

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            simbaElement.classList.add("open");
          } else {
            simbaElement.classList.remove("open");
          }
        });
      },
      {
        threshold: 0.1,
      },
    );

    sectionObserver.observe(parentSection);
  });
};

// 関数を実行
initSimbaAnimation();

// Intersection Observer APIを使用して.js-simba-doorが画面内に入った場合に処理を行う
const simbaDoorElements = document.querySelectorAll(".js-simba-door");
if (simbaDoorElements.length > 0) {
  const simbaDoorObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("open");
        } else {
          entry.target.classList.remove("open");
        }
      });
    },
    {
      rootMargin: "40% 15%",
      threshold: 0.1, // 要素の10%が見えるときにコールバック
    },
  );

  // 各ドア要素を監視
  simbaDoorElements.forEach((doorElement) => {
    simbaDoorObserver.observe(doorElement);
  });
}

// Intersection Observer APIを使用してfooterが画面内に入った場合に.simba-logo-wrapにopenクラスを付与
const footerElement = document.querySelector(".footer");
const simbaLogoWrap = document.querySelector(".simba-logo-wrap");
if (footerElement && simbaLogoWrap) {
  const footerObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          simbaLogoWrap.classList.add("open");
        } else {
          simbaLogoWrap.classList.remove("open");
        }
      });
    },
    {
      threshold: 0, // フッターの一部でも見えたら発火
    },
  );
  footerObserver.observe(footerElement);
}

document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
  anchor.addEventListener("click", function (e) {
    const href = this.getAttribute("href");
    const target = document.querySelector(href);

    if (!target) return;

    e.preventDefault();

    const currentScroll = window.pageYOffset;
    const targetRectTop = target.getBoundingClientRect().top;
    const targetOffsetTop = targetRectTop + currentScroll;


    let offset = mq("pc") ? 38 : (93 / 390) * window.innerWidth;
    if (target.id === "ufo") {
      offset += mq("pc") ? 38 : (40 / 390) * window.innerWidth;;
    }

    const scrollTarget = targetOffsetTop - offset;

    window.scrollTo({
      top: scrollTarget,
      behavior: "smooth",
    });
  });
});

// if (mq("pc")) {
//   // スクロールイベントリスナー
//   window.addEventListener("scroll", () => {
//     // リクエストアニメーションフレームを使用して負荷を軽減
//     window.requestAnimationFrame(() => {
//       const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

//       if (currentScrollTop < lastScrollTop) {
//         // 上にスクロールしているときにopenを付ける
//         document.body.classList.add("open");
//       } else {
//         // 下にスクロールしているときにopenを削除
//         // スマホの場合は遅延を追加
//         if (window.innerWidth <= 768) {
//           // 既存のタイムアウトをクリア
//           if (scrollTimeout) {
//             clearTimeout(scrollTimeout);
//           }
//           // 新しいタイムアウトを設定
//           scrollTimeout = setTimeout(() => {
//             document.body.classList.remove("open");
//             scrollTimeout = null;
//           }, 100); // スマホでは100ms遅延
//         } else {
//           document.body.classList.remove("open");
//         }
//       }

//       lastScrollTop = currentScrollTop <= 0 ? 0 : currentScrollTop; // Safariの負のスクロール値を防ぐ
//     });
//   });
// }

// 翻訳テキストの高さを再設定する関数
const updateTranslateTextHeight = (translateText) => {
  // 一時的にテキストを表示して高さを取得
  const originalContent = translateText.innerHTML;
  translateText.style.visibility = "hidden";
  translateText.style.position = "absolute";
  translateText.innerHTML = originalContent;

  const height = translateText.offsetHeight;

  translateText.style.minHeight = height + "px";

  // 元の状態に戻す
  translateText.style.visibility = "visible";
  translateText.style.position = "static";
  gsap.set(translateText, { opacity: 1 });
  // テキストは空にしない（startTypewriterAnimationで処理する）
};

const setTranslateTextWrapMaxHeight = (translateText) => {
  const wrap = translateText.closest(".translate-text-wrap");
  if (!wrap) return;

  wrap.style.setProperty("--max-height", `${translateText.offsetHeight}px`);
};

// シャッフルテキスト機能
const shuffleTextAnimation = () => {
  const shuffleElements = document.querySelectorAll(".js-shuffle-text");

  shuffleElements.forEach((element) => {
    const textElement = element.querySelector("span");
    if (!textElement) return;

    const originalText = textElement.textContent;
    const translateText = textElement.getAttribute("data-text");
    let shuffleInstance = null;

    element.addEventListener("mouseenter", () => {
      textElement.classList.add("translate");
      if (translateText) {
        textElement.textContent = translateText;
      }
      // ホバー開始時にシャッフルアニメーション開始
      shuffleInstance = new ShuffleText(textElement, {
        duration: 0.6,
        characterClass: "shuffle-character",
      });

      shuffleInstance.start();

      // シャッフル完了後にクラスとテキストを変更
      // setTimeout(() => {
      //   textElement.classList.add('translate');
      //   if (translateText) {
      //     textElement.textContent = translateText;
      //   }
      // }, 300);
    });

    element.addEventListener("mouseleave", () => {
      textElement.textContent = originalText;
      // ホバー終了時にシャッフルアニメーション開始
      const leaveShuffleInstance = new ShuffleText(textElement, {
        duration: 0.6,
        characterClass: "shuffle-character",
      });

      leaveShuffleInstance.start();
      textElement.classList.remove("translate");

      if (shuffleInstance) {
        shuffleInstance.stop();
      }
    });
  });
};

// シャッフルリンク機能（IntersectionObserver使用）
const shuffleLinkAnimation = () => {
  const shuffleLinkElements = document.querySelectorAll(".js-shuffle-link");

  shuffleLinkElements.forEach((element) => {
    const textElement = element.querySelector("span");
    if (!textElement) return;

    const originalText = textElement.textContent;
    const translateText = textElement.getAttribute("data-text");
    let hasAnimated = false; // 一度だけアニメーションするためのフラグ

    // IntersectionObserverで画面内に入ったらシャッフル実行
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated && translateText) {
            hasAnimated = true;

            // 変換中のクラスを追加
            textElement.classList.add("converting");

            // テキストを先に日本語に変更
            textElement.textContent = translateText;
            textElement.classList.add("translate");

            // シャッフルアニメーション開始（js-shuffle-textと同じ方式）
            const shuffleInstance = new ShuffleText(textElement, {
              duration: 0.8,
              characterClass: "shuffle-character",
            });

            shuffleInstance.start();

            // 変換中のクラスを削除
            textElement.classList.remove("converting");

            // 一度アニメーションしたら監視を停止
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: mq("pc") ? "0% 0% -30% 0%" : "0% 0% -20% 0%", // 画面の下30%の位置まで入ったら発火
        threshold: 0.1, // 要素の10%が見えたら発火
      },
    );

    observer.observe(element);
  });
};

const SIMBA_PROGRESS_START = 14;
const SIMBA_PROGRESS_END = 100;
const SIMBA_TRANSLATE_DURATION = 1;

let progressBarStarted = false;

const formatTranslateProgress = (percent) => `自動翻訳中(${percent}/100%)...`;

const usesSourceTranslateAnimation = (element) => Boolean(element.querySelector(".js-auto-translate-source"));

const shouldTriggerUfoProgressBar = (element) => {
  if (progressBarStarted) return false;
  if (!document.querySelector("#simba-progress-bar-wrap")) return false;

  const ufoSection = element.closest(".bg-ufo");
  if (!ufoSection) return false;

  const firstSlideTranslate = ufoSection.querySelector(
    ".step-embla .embla__slide:first-child .js-auto-translate",
  );
  return firstSlideTranslate === element;
};

const triggerUfoProgressBar = (element) => {
  if (!shouldTriggerUfoProgressBar(element)) return;

  progressBarStarted = true;
  setTimeout(() => {
    progressBarAnimation();
  }, 300);
};

const startSimbaIntroAnimation = (element) => {
  const sourceText = element.querySelector(".js-auto-translate-source");
  const translateTitle = element.querySelector(".js-auto-translate-title");
  const translateText = element.querySelector(".js-auto-translate-text");

  if (!sourceText || !translateTitle || !translateText) return;

  const onIntroComplete = () => {
    triggerUfoProgressBar(element);
  };

  gsap.set(sourceText, { opacity: 1 });
  gsap.set(translateText, { opacity: 0 });
  updateTranslateTextHeight(translateText);
  gsap.set(translateText, { opacity: 0 });

  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  if (prefersReducedMotion) {
    gsap.set(sourceText, { opacity: 0 });
    gsap.set(translateText, { opacity: 1 });
    translateTitle.textContent = "（自動翻訳）";
    translateTitle.classList.add("is-complete");
    setTranslateTextWrapMaxHeight(translateText);
    onIntroComplete();
    return;
  }

  const blinkTimeline = gsap
    .timeline({ repeat: -1 })
    .to(sourceText, { opacity: 0.3, duration: 0.25, ease: "power2.inOut" })
    .to(sourceText, { opacity: 1, duration: 0.25, ease: "power2.inOut" });

  const progressState = { value: SIMBA_PROGRESS_START };
  translateTitle.textContent = formatTranslateProgress(SIMBA_PROGRESS_START);
  translateTitle.classList.remove("is-complete");
  gsap.set(translateTitle, { opacity: 1 });

  const mainTimeline = gsap.timeline({
    onComplete: () => {
      blinkTimeline.kill();
      translateTitle.textContent = "自動翻訳";
      translateTitle.classList.add("is-complete");
      setTranslateTextWrapMaxHeight(translateText);
      gsap.set(translateTitle, {opacity: 0});
      gsap.to(translateTitle, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: "power2.out",
      });
      onIntroComplete();
    },
  });

  mainTimeline.to(progressState, {
    value: SIMBA_PROGRESS_END,
    duration: SIMBA_TRANSLATE_DURATION,
    ease: "power2.out",
    onUpdate: () => {
      const percent = Math.round(progressState.value);

      if (percent >= 100) {
        // translateTitle.textContent = "";
        gsap.to(translateTitle, {
        opacity: 0,
        y: 10,
        duration: 0.8,
        ease: "power2.out",
      });
        // gsap.set(translateTitle, { opacity: 0 });
        return;
      }

      translateTitle.textContent = formatTranslateProgress(percent);
      gsap.set(translateTitle, { opacity: 1 });
    },
  });

  mainTimeline.to(
    sourceText,
    {
      opacity: 0,
      duration: 0.4,
      ease: "power2.inOut",
    },
    "-=0.1",
  );

  mainTimeline.to(
    translateText,
    {
      opacity: 1,
      duration: 0.4,
      ease: "power2.inOut",
    },
    "<",
  );
};

const simbaIntroTranslateAnimation = () => {
  document.querySelectorAll(".js-auto-translate").forEach((element) => {
    if (!usesSourceTranslateAnimation(element)) return;

    const translateText = element.querySelector(".js-auto-translate-text");
    if (translateText) {
      gsap.set(translateText, { opacity: 0 });
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startSimbaIntroAnimation(element);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.5,
      },
    );

    observer.observe(element);
  });
};

// ドラクエ風テキストアニメーション機能
const typewriterAnimation = () => {
  const autoTranslateElements = document.querySelectorAll(".js-auto-translate");

  autoTranslateElements.forEach((element) => {
    if (usesSourceTranslateAnimation(element)) return;

    const fontSimbaText = element.querySelector(".font-simba");
    const translateTitle = element.querySelector(".js-auto-translate-title");
    const translateText = element.querySelector(".js-auto-translate-text");
    const goalText = element.querySelector("#simba-goal-text");

    if (!fontSimbaText || !translateTitle || !translateText) return;

    // 初期状態を設定
    // gsap.set(fontSimbaText, { opacity: 0 });
    gsap.set(translateText, { opacity: 0 });

    // 翻訳テキストの高さを初期設定
    updateTranslateTextHeight(translateText);

    // タイトルを「（自動翻訳中）」に変更して点滅開始
    translateTitle.textContent = "（自動翻訳中）";
    gsap.set(translateTitle, { opacity: 1 });

    // 点滅アニメーションを繰り返し
    const blinkTimeline = gsap.timeline({ repeat: -1 });

    // Intersection Observerで画面内に入ったら実行
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startTypewriterAnimation(fontSimbaText, translateTitle, translateText, blinkTimeline, goalText);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.5,
      },
    );

    observer.observe(element);
  });

  // リサイズ時に高さを再設定
  let resizeTimeout;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      autoTranslateElements.forEach((element) => {
        if (usesSourceTranslateAnimation(element)) return;

        const translateText = element.querySelector(".js-auto-translate-text");
        if (translateText) {
          updateTranslateTextHeight(translateText);
        }
      });

      document.querySelectorAll(".js-auto-translate-source").forEach((sourceText) => {
        const element = sourceText.closest(".js-auto-translate");
        const translateText = element?.querySelector(".js-auto-translate-text");
        if (!translateText) return;

        const opacity = gsap.getProperty(translateText, "opacity") ?? 0;
        updateTranslateTextHeight(translateText);
        gsap.set(translateText, { opacity });
      });
    }, 250); // リサイズ完了後250msで実行
  });
};

// タイプライターアニメーションの実行
const startTypewriterAnimation = (fontSimbaText, translateTitle, translateText, blinkTimeline, goalText) => {
  const originalText = fontSimbaText.innerHTML;
  const originalTranslateText = translateText.innerHTML;

  // 元のテキストを保存
  const originalTranslateTextContent = originalTranslateText;

  // 初期状態
  translateText.innerHTML = "";
  gsap.set(fontSimbaText, { opacity: 1 });

  // 点滅アニメーション
  blinkTimeline
    .to(translateTitle, {
      opacity: 0.4,
      duration: 0.5,
      ease: "power2.inOut",
    })
    .to(translateTitle, {
      opacity: 1,
      duration: 0.5,
      ease: "power2.inOut",
    });

  // HTMLタグを保持しながら1文字ずつ表示する関数
  const typeChar = (text, element, interval, onComplete) => {
    const sourceText = String(text ?? "");
    let currentIndex = 0;
    let currentText = "";

    // HTMLタグを正規表現で検出
    const tagRegex = /<br\s*\/?[^>]*>/gi;
    const textWithoutTags = sourceText.replace(tagRegex, "");
    const chars = textWithoutTags.split("");

    const typeInterval = setInterval(() => {
      if (currentIndex < chars.length) {
        currentText += chars[currentIndex];

        // 現在の文字位置までのタグを挿入
        let resultText = "";
        let charCount = 0;

        for (let i = 0; i < sourceText.length; i++) {
          const char = sourceText[i];
          if (char === "<") {
            // タグの開始
            const tagEnd = sourceText.indexOf(">", i);
            if (tagEnd !== -1) {
              const tag = sourceText.substring(i, tagEnd + 1);
              resultText += tag;
              i = tagEnd;
            }
          } else if (char !== ">") {
            // 通常の文字
            if (charCount < currentText.length) {
              resultText += char;
              charCount++;
            }
          }
        }

        element.innerHTML = resultText;
        currentIndex++;
      } else {
        clearInterval(typeInterval);
        if (onComplete) onComplete();
      }
    }, interval); // 50ms間隔で1文字ずつ
  };

  // 文字数に応じて表示速度を計算する関数
  const calculateSpeed = (text) => {
    const sourceText = String(text ?? "");
    // HTMLタグを除いた実際の文字数をカウント
    const textWithoutTags = sourceText.replace(/<br\s*\/?[^>]*>/gi, "");
    const charCount = textWithoutTags.length;

    // 線形補間を使用した滑らかな速度調整（微調整可能）
    const speedConfig = {
      minSpeed: 40, // 最小速度（ゆっくり）- 調整可能
      maxSpeed: 20, // 最大速度（高速）- 調整可能
      minChars: 40, // 最小文字数 - 調整可能
      maxChars: 300, // 最大文字数 - 調整可能
      speedMultiplier: 0.5, // 全体の速度倍率 - 調整可能
      curve: 1.6, // カーブ調整（1.0=線形, >1.0=高速寄り, <1.0=低速寄り） - 調整可能
    };

    // 文字数に応じて線形補間で速度を計算
    const normalizedChars = Math.max(speedConfig.minChars, Math.min(speedConfig.maxChars, charCount));
    const progress = (normalizedChars - speedConfig.minChars) / (speedConfig.maxChars - speedConfig.minChars);

    // カーブ調整を適用
    const curvedProgress = Math.pow(progress, speedConfig.curve);

    // 線形補間で速度を計算
    const speed = speedConfig.maxSpeed + (speedConfig.minSpeed - speedConfig.maxSpeed) * (1 - curvedProgress);

    // 速度倍率を適用して最終速度を計算
    const finalSpeed = Math.round(speed * speedConfig.speedMultiplier);

    return finalSpeed;
  };

  // font-simbaの表示速度を計算
  const fontSimbaSpeed = calculateSpeed(originalTranslateTextContent);

  // アニメーション開始
  typeChar(originalTranslateText, translateText, fontSimbaSpeed, () => {
    // font-simbaテキスト表示完了後

    // blinkTimeline.kill(); // 点滅停止
    translateTitle.textContent = "（自動翻訳中）";

    gsap.set(translateTitle, { opacity: 1 });

    // // 翻訳テキストを一度に表示してフェードイン
    // translateText.innerHTML = originalTranslateTextContent;
    // gsap.set(translateText, { opacity: 0, transform: "translateY(20px)" });
    // gsap.to(translateText, {
    //   opacity: 1,
    //   transform: "translateY(0)",
    //   duration: 0.8,
    //   delay: 0.2,
    //   ease: "power2.out",
    // });

    // 0.2秒後に翻訳テキスト表示開始
    setTimeout(() => {
      // 翻訳完了時のログ

      blinkTimeline.kill();
      gsap.set(translateTitle, { opacity: 1 });
      translateTitle.textContent = "（自動翻訳）";
    }, 200);
  });

  if (goalText) {
    if (!progressBarStarted) {
      progressBarStarted = true;
      setTimeout(() => {
        progressBarAnimation();
      }, 300);
    }
    return;
  }

  triggerUfoProgressBar(fontSimbaText.closest(".js-auto-translate"));
};

// アコーディオン機能
const accordionFunc = () => {
  const accordionBtn = document.querySelector(".js-accordion-btn");
  const accordionItems = document.querySelectorAll(".js-accordion-hidden");

  // 要素が4件未満の場合はボタンを非表示にする
  if (accordionItems.length === 0) {
    if (accordionBtn) {
      accordionBtn.style.display = "none";
    }
    return;
  }

  if (!accordionBtn) return;

  let isExpanded = false;

  accordionBtn.addEventListener("click", (e) => {
    e.preventDefault();

    if (!isExpanded) {
      // アコーディオンを展開
      gsap.set(accordionItems, {
        display: "block",
        opacity: 0,
        y: 30,
        scale: 0.95,
      });

      gsap.to(accordionItems, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.8,
        ease: "power2.out",
        stagger: 0.15,
      });

      accordionBtn.classList.toggle("is-open");
      accordionBtn.classList.toggle("is-close");

      // ボタンテキストを変更
      const span = accordionBtn.querySelector("span");
      if (span) {
        span.textContent = "とじる";
      }

      isExpanded = true;
    } else {
      accordionBtn.classList.toggle("is-open");
      accordionBtn.classList.toggle("is-close");

      // アコーディオンを閉じる
      gsap.to(accordionItems, {
        opacity: 0,
        y: -20,
        scale: 0.9,
        duration: 0.5,
        ease: "power2.in",
        stagger: 0.1,
        onComplete: () => {
          gsap.set(accordionItems, {
            display: "none",
          });
        },
      });

      // ボタンテキストを元に戻す
      const span = accordionBtn.querySelector("span");
      if (span) {
        span.textContent = "ひらく";
      }

      isExpanded = false;
    }
  });
};

const initSimbaProgressBarObserver = () => {
  const progressBarWrap = document.querySelector("#simba-progress-bar-wrap");
  if (!progressBarWrap) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || progressBarStarted) return;

        progressBarStarted = true;
        setTimeout(() => {
          progressBarAnimation();
        }, 300);
        observer.unobserve(entry.target);
      });
    },
    {
      threshold: 0.5,
    },
  );

  observer.observe(progressBarWrap);
};

// プログレスバーのアニメーション機能
const progressBarAnimation = () => {
  const progressBar = document.querySelector("#simba-progress-bar");

  if (!progressBar) return;

  const progressBarWrap = document.querySelector("#simba-progress-bar-wrap");
  const progressBarInner = document.querySelector("#simba-progress-bar-inner");
  const progressBarText = document.querySelector("#simba-progress-bar-text");
  const targetValue = parseInt(progressBar.getAttribute("data-value"), 10);

  if (!progressBarWrap || !progressBarInner || !progressBarText || Number.isNaN(targetValue)) return;

  // 初期状態を設定
  gsap.set(progressBarInner, { width: "0%" });
  progressBarText.textContent = "0";

  progressBarWrap.classList.add("active");
  setTimeout(() => {
    animateProgressBar(targetValue, progressBarText);
    gsap.to(progressBarInner, {
      width: `${targetValue}%`,
      duration: 3,
      ease: "power1.out",
    });
  }, 200);
};

// プログレスバーのアニメーション実行
const animateProgressBar = (targetValue, progressBarText) => {
  // カウントアップアニメーション
  gsap.to(progressBarText, {
    textContent: targetValue,
    duration: 3,
    ease: "power1.out",
    snap: { textContent: 1 }, // 整数でスナップ
  });
};

// DOMが完全にロードされたらbodyに'open'クラスを追加
document.addEventListener("DOMContentLoaded", async () => {
  if (BODY[0].classList.contains("open")) return;

  // ヘッダー上部のニュースティッカー・ニュースエリアの一覧を初期化
  await initFixedNewsTicker();
  await initNewsList();

  // EmblaCarouselの初期化フラグ
  let carouselInitialized = false;

  // EmblaCarouselの初期化コールバック
  const initCarouselCallback = () => {
    if (carouselInitialized) return; // 重複初期化を防ぐ
    initEmblaCarousel();
    initStepEmblaCarousel();
    carouselInitialized = true;
  };

  // セッションストレージの状態をチェック
  const hasVisited = sessionStorage.getItem("simba-video-played");

  if (hasVisited) {
    // 既に訪問済みの場合は1秒後にEmblaCarouselを初期化
    setTimeout(() => {
      initCarouselCallback();
    }, 400);

    // 動画プレイヤーを初期化（初回訪問時のみコールバック付き）
    new VideoPlayer(); // コールバックなし
  } else {
    new VideoPlayer(initCarouselCallback); // コールバック付き
  }

  hbgFunc();
  // accordionFunc();
  shuffleTextAnimation();
  shuffleLinkAnimation();
  simbaIntroTranslateAnimation();
  initSimbaProgressBarObserver();
  setTimeout(() => {
    typewriterAnimation();
  }, 1000);
});
